import Phaser from 'phaser';
import type { AthleteView, Pose } from './AthleteView';
import { CosmeticView } from './CosmeticView';

const TAU = Math.PI * 2;

/**
 * Whole-body mascot renderer: a single Higgsfield sprite animated procedurally with
 * squash & stretch, vertical bob, and forward lean. The static legs in the artwork read
 * as "running" thanks to the bounce — a robust, premium look for a side-scroller.
 *
 * The inner sprite has its origin at the feet (bottom-centre); the container sits on the
 * ground line so the scene only moves container.x.
 */
export class MascotSprite implements AthleteView {
  readonly container: Phaser.GameObjects.Container;
  private readonly sprite: Phaser.GameObjects.Sprite;
  private baseScale: number;
  private readonly displayHeight: number;
  private phase = 0;
  private speedNorm = 0;
  private pose: Pose = 'idle';
  private facing = 1;
  private cosmetics?: CosmeticView;

  constructor(
    scene: Phaser.Scene, x: number, y: number, textureKey: string, displayHeight: number,
    opts?: { skinId?: string; equipped?: Record<string, string> },
  ) {
    this.displayHeight = displayHeight;
    this.sprite = scene.add.sprite(0, 0, textureKey).setOrigin(0.5, 1);
    this.baseScale = displayHeight / this.sprite.height;
    this.sprite.setScale(this.baseScale);
    this.container = scene.add.container(x, y, [this.sprite]);
    if (opts?.skinId && opts.equipped && Object.keys(opts.equipped).length) {
      this.cosmetics = new CosmeticView(scene, this.container, this.sprite, displayHeight, this.sprite.width * this.baseScale, opts.skinId, opts.equipped);
    }
  }

  /** Swap the body texture (e.g. a head-accessory variant loaded on demand), keeping feet anchored. */
  swapBody(textureKey: string): void {
    if (!this.sprite.scene.textures.exists(textureKey)) return;
    this.sprite.setTexture(textureKey);
    this.baseScale = this.displayHeight / this.sprite.height;
    this.sprite.setScale(this.facing * this.baseScale, this.baseScale);
  }

  setMotion(speedNorm: number): void {
    this.speedNorm = Phaser.Math.Clamp(speedNorm, 0, 1);
    if (this.pose === 'idle' && this.speedNorm > 0.02) this.pose = 'run';
    else if (this.pose === 'run' && this.speedNorm <= 0.02) this.pose = 'idle';
  }

  setPose(pose: Pose): void {
    this.pose = pose;
  }

  setFacing(facing: number): void {
    this.facing = facing < 0 ? -1 : 1;
  }

  update(dt: number): void {
    switch (this.pose) {
      case 'run':
        this.applyRun(dt);
        break;
      case 'celebrate':
        this.applyCelebrate(dt);
        break;
      case 'exhausted':
        this.applyExhausted(dt);
        break;
      default:
        this.applyIdle(dt);
    }
    this.cosmetics?.update(dt, this.pose === 'run' ? Math.max(this.speedNorm, 0.3) : this.speedNorm);
  }

  private applyIdle(dt: number): void {
    this.phase += dt * 2.2;
    const breathe = Math.sin(this.phase) * 0.015;
    this.sprite.setScale(this.facing * this.baseScale * (1 - breathe), this.baseScale * (1 + breathe));
    this.sprite.x = 0;
    this.sprite.y = 0;
    this.sprite.rotation = Math.sin(this.phase * 0.5) * 0.01;
  }

  private applyRun(dt: number): void {
    const sn = this.speedNorm;
    const freqHz = 2.8 + sn * 7;
    this.phase += dt * freqHz * TAU;
    const p = this.phase;

    // Two foot-strikes per stride with a SHAPED bounce: quick landing, floatier apex (reads as a
    // real gallop rather than a sine bob). Bigger vertical travel + push-off lunge sell the speed.
    const raw = Math.sin(p * 2);
    const bounce = Math.sign(raw) * Math.pow(Math.abs(raw), 0.7);
    const bob = Phaser.Math.Linear(6, 34, sn);
    this.sprite.y = -(0.5 + 0.5 * bounce) * bob;

    // Stretch tall at the apex, squash wide on contact.
    const st = bounce * 0.08 * sn;
    this.sprite.setScale(this.facing * this.baseScale * (1 - st), this.baseScale * (1 + st));

    // Forward lean grows with speed, plus a stride-synced pitch wobble and a small horizontal
    // push-off lunge (local — never moves the world position the discipline controls).
    this.sprite.rotation = this.facing * (Phaser.Math.Linear(0.03, 0.2, sn) + Math.sin(p) * 0.045 * sn);
    this.sprite.x = this.facing * Math.sin(p) * 4 * sn;
  }

  private applyCelebrate(dt: number): void {
    this.phase += dt * 7;
    this.sprite.x = 0;
    const j = Math.abs(Math.sin(this.phase));
    this.sprite.y = -j * 40;
    const s = 1 + j * 0.05;
    this.sprite.setScale(this.facing * this.baseScale / s, this.baseScale * s);
    this.sprite.rotation = Math.sin(this.phase * 2) * 0.08;
  }

  private applyExhausted(dt: number): void {
    this.phase += dt * 3;
    this.sprite.x = 0;
    const pant = Math.sin(this.phase) * 0.03;
    this.sprite.y = 4;
    this.sprite.setScale(this.facing * this.baseScale * (1 + pant), this.baseScale * (1 - pant * 0.5));
    this.sprite.rotation = this.facing * 0.06;
  }

  destroy(): void {
    this.container.destroy();
  }
}
